import React, { useState } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { providerTemplates } from './provider/providerTemplates';
import { validateProviderConfig, hasValidationErrors, ValidationError } from './provider/providerValidation';
import ProviderSelector from './provider/ProviderSelector';
import ProviderFieldInput from './provider/ProviderFieldInput';
import CloudflareAuthConfig from './provider/CloudflareAuthConfig';
import ConnectionTest from './provider/ConnectionTest';
import FormActions from './provider/FormActions';

interface ProviderConfigFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (providerData: any) => Promise<void>;
  mode?: 'add' | 'edit';
  initial?: { name?: string; type?: string };
}

interface ProviderFormData {
  name: string;
  type: string;
  credentials: Record<string, string>;
  settings: Record<string, any>;
}

const ProviderConfigForm: React.FC<ProviderConfigFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode = 'add',
  initial,
}) => {
  const [formData, setFormData] = useState<ProviderFormData>({
    name: initial?.name || '',
    type: initial?.type || '',
    credentials: {},
    settings: {},
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<ValidationError>({});
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [cfAuthMode, setCfAuthMode] = useState<'user' | 'account'>('user');

  const selectedProvider = formData.type ? providerTemplates[formData.type] : null;
  const isCloudflare = formData.type === 'cloudflare';

  if (!isOpen) return null;

  const handleProviderTypeChange = (type: string) => {
    setFormData({
      name: providerTemplates[type as keyof typeof providerTemplates]?.name || '',
      type,
      credentials: {},
      settings: {},
    });
    setErrors({});
    setConnectionStatus('idle');
  };

  const handleCredentialChange = (key: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      credentials: { ...prev.credentials, [key]: value },
    }));
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const validateForm = () => {
    const validationErrors: ValidationError = {};

    if (!formData.name.trim()) {
      validationErrors.name = 'Provider name is required';
    }

    if (!formData.type) {
      validationErrors.type = 'Provider type is required';
    }

    if (selectedProvider) {
      const providerErrors = validateProviderConfig(selectedProvider.fields, formData.credentials);
      Object.assign(validationErrors, providerErrors);
    }

    setErrors(validationErrors);
    return !hasValidationErrors(validationErrors);
  };

  const testConnection = async () => {
    if (!validateForm()) return;

    setTestingConnection(true);
    setConnectionStatus('idle');

    try {
      // Connection testing is disabled; add the provider first and run tests from the provider details.
      setConnectionStatus('error');
      setErrors({
        ...errors,
        connection: 'Connection test is disabled. Add provider first and test via provider endpoints.',
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        ...formData,
        settings: {
          auto_sync: true,
          sync_interval: 300, // 5 minutes
          cloudflare_token_type: isCloudflare ? cfAuthMode : undefined,
          ...formData.settings,
        },
      });
      onClose();
      // Reset form
      setFormData({ name: '', type: '', credentials: {}, settings: {} });
      setErrors({});
      setConnectionStatus('idle');
    } catch (error: any) {
      const serverMsg = error?.response?.data?.error || error?.response?.data?.message;
      setErrors({ submit: serverMsg || error.message || 'Failed to add provider' });
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {mode === 'add' ? 'Add DNS Provider' : 'Edit DNS Provider'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Provider Type Selection */}
          <ProviderSelector
            selectedProvider={formData.type}
            onProviderSelect={handleProviderTypeChange}
          />
          {errors.type && <p className="text-red-500 text-sm mt-1">{errors.type}</p>}

          {/* Provider Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Provider Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
              placeholder="e.g., Production Cloudflare"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          {/* Provider Configuration */}
          {selectedProvider && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Configuration</h3>
                <a
                  href={selectedProvider.docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-blue-600 hover:text-blue-700 text-sm"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Documentation
                </a>
              </div>

              {isCloudflare ? (
                <CloudflareAuthConfig
                  cfAuthMode={cfAuthMode}
                  setCfAuthMode={setCfAuthMode}
                  apiToken={formData.credentials.api_token || ''}
                  onTokenChange={(value) => handleCredentialChange('api_token', value)}
                  error={errors.api_token}
                />
              ) : (
                <div className="space-y-4">
                  {selectedProvider.fields.map((field) => (
                    <ProviderFieldInput
                      key={field.key}
                      field={field}
                      value={formData.credentials[field.key] || ''}
                      onChange={(value) => handleCredentialChange(field.key, value)}
                      error={errors[field.key]}
                    />
                  ))}
                </div>
              )}

              {/* Connection Test */}
              <ConnectionTest
                onTest={testConnection}
                isLoading={testingConnection}
                status={connectionStatus}
                errorMessage={errors.connection}
              />
            </div>
          )}

          <FormActions
            isSubmitting={isSubmitting}
            hasProviderSelected={!!formData.type}
            mode={mode}
            onCancel={onClose}
            submitError={errors.submit}
          />
        </form>
      </div>
    </div>
  );
};

export default ProviderConfigForm;
